import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bold, ImagePlus, Italic, Link2, List } from "lucide-react";
import { Button } from "../../ui/Button";
import { Input } from "../../ui/Input";
import { Textarea } from "../../ui/Textarea";
import { cn } from "../../../utils/cn";
import { ANNOUNCEMENT_IMAGE_TYPES, uploadAnnouncementImage } from "../../../api/announcements";
import { applyFormat } from "./markdownFormat";
import { isAllowedLink, LIMITS } from "./announcementForm";

// ─────────────────────────────────────────────────────────────────────────────
// Editor del texto del pop-up: textarea de Markdown con una barra mínima
// (negrita, cursiva, lista, link, imagen). Sin editor WYSIWYG a propósito: el
// contenido se guarda como Markdown y nunca como HTML (ver
// RemoteAnnouncementContent). La vista previa del editor muestra el resultado.
// ─────────────────────────────────────────────────────────────────────────────

function ToolbarButton({ label, icon, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className={cn(
        "flex h-8 w-8 items-center justify-center rounded-md text-fg-muted",
        "hover:bg-bg-muted hover:text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
        "disabled:cursor-not-allowed disabled:opacity-50",
      )}
    >
      {icon}
    </button>
  );
}

/**
 * @param {{ id: string, value: string, onChange: (v: string) => void,
 *   ownerKey: string, invalid?: boolean, describedBy?: string }} props
 */
export default function MarkdownEditor({ id, value, onChange, ownerKey, invalid, describedBy }) {
  const { t } = useTranslation();
  const textareaRef = useRef(null);
  const fileRef = useRef(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("https://");
  const [linkError, setLinkError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const selection = () => {
    const el = textareaRef.current;
    return el ? [el.selectionStart ?? value.length, el.selectionEnd ?? value.length] : [value.length, value.length];
  };

  const apply = (kind, extra) => {
    const [start, end] = selection();
    const result = applyFormat(value, start, end, kind, extra);
    onChange(result.value);
    // Restaurar foco y selección después de que React pinte el valor nuevo.
    window.requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(result.selStart, result.selEnd);
    });
  };

  const insertLink = () => {
    if (!isAllowedLink(linkUrl)) {
      setLinkError(t("El link debe empezar con https://, mailto: o tel:"));
      return;
    }
    apply("link", { url: linkUrl.trim(), text: t("texto del link") });
    setLinkOpen(false);
    setLinkUrl("https://");
    setLinkError("");
  };

  const onPickImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setUploadError("");
    setUploading(true);
    const result = await uploadAnnouncementImage(file, ownerKey);
    setUploading(false);
    if (!result.ok) {
      setUploadError(t(result.error));
      return;
    }
    apply("image", { url: result.url, text: t("imagen") });
  };

  return (
    <div className="flex flex-col gap-2">
      <div
        role="toolbar"
        aria-label={t("Formato del texto")}
        className="flex flex-wrap items-center gap-1 rounded-lg border border-border bg-bg-muted px-1 py-1"
      >
        <ToolbarButton label={t("Negrita")} icon={<Bold className="h-4 w-4" />} onClick={() => apply("bold", { text: t("texto") })} />
        <ToolbarButton label={t("Cursiva")} icon={<Italic className="h-4 w-4" />} onClick={() => apply("italic", { text: t("texto") })} />
        <ToolbarButton label={t("Lista")} icon={<List className="h-4 w-4" />} onClick={() => apply("list", { text: t("elemento") })} />
        <ToolbarButton label={t("Insertar link")} icon={<Link2 className="h-4 w-4" />} onClick={() => setLinkOpen((open) => !open)} />
        <ToolbarButton
          label={t("Insertar imagen")}
          icon={<ImagePlus className="h-4 w-4" />}
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
        />
        <input
          ref={fileRef}
          type="file"
          accept={Object.keys(ANNOUNCEMENT_IMAGE_TYPES).join(",")}
          className="hidden"
          onChange={onPickImage}
          aria-hidden="true"
          tabIndex={-1}
        />
        {uploading && <span className="px-2 text-xs text-fg-muted">{t("Subiendo imagen…")}</span>}
      </div>

      {linkOpen && (
        <div className="flex flex-col gap-1">
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              size="sm"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              aria-label={t("Dirección del link")}
              invalid={Boolean(linkError)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  insertLink();
                }
              }}
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={insertLink}>
                {t("Insertar")}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setLinkOpen(false)}>
                {t("Cancelar")}
              </Button>
            </div>
          </div>
          {linkError && <span className="text-xs text-status-error">{linkError}</span>}
        </div>
      )}

      <Textarea
        id={id}
        ref={textareaRef}
        rows={8}
        value={value}
        maxLength={LIMITS.body}
        onChange={(e) => onChange(e.target.value)}
        invalid={invalid}
        aria-describedby={describedBy}
        placeholder={t("Escribe el mensaje. Usa la barra para dar formato, agregar links o imágenes.")}
      />
      <div className="flex items-center justify-between gap-2 text-xs text-fg-muted">
        <span>{uploadError ? <span className="text-status-error">{uploadError}</span> : t("Admite formato Markdown.")}</span>
        <span className="tabular-nums">
          {value.length}/{LIMITS.body}
        </span>
      </div>
    </div>
  );
}
